import { rem } from 'polished'
import { useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Pane from 'components/Common/Pane'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import TextButton from 'LuxKit/Button/TextButton'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import Pill from 'LuxKit/Pill'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LoyaltySwitchRegionPrompt from 'loyalty/components/LoyaltySwitchRegionPrompt'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useLoyaltyPointsPurchaseBestOffer from 'loyalty/hooks/useLoyaltyPointsPurchaseBestOffer'
import {
  computeMaldivesRewardsPayCalculation,
  type MaldivesRewardsPayCalculation,
  type MaldivesRewardsPayTierPricing,
} from 'loyalty/lib/maldivesRewardsPayCalculator/computeMaldivesRewardsPayCalculator'

const CalculatorCard = styled(Pane)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  overflow: hidden;
`

const CardHeader = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const CardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const WorkingColumn = styled.div`
  padding: ${rem(20)};
`

const OutcomeColumn = styled.div`
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
`

const SectionLabel = styled(Caption)`
  display: block;
  margin-bottom: ${rem(8)};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(4)} 0;
`

const OutcomeRow = styled(Row)`
  align-items: flex-start;
`

const OutcomeRowLabel = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: 62%;
`

const OutcomeRowValue = styled.div`
  flex-shrink: 0;
`

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: ${rem(12)} 0;
`

const TopUpPrompt = styled.div`
  margin-top: ${rem(12)};
`

const CoveredByBalancePill = styled.div`
  margin-top: ${rem(8)};
`

const OutcomeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const RewardsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
`

const RewardsSectionLabel = styled(SectionLabel)`
  display: inline-block;
  margin-bottom: 0;
  line-height: ${rem(20)};
`

const SavingsPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const HeroPrice = styled(BodyText)`
  color: ${props => props.theme.palette.highlight.primary.darkBackground};
  font-weight: ${props => props.theme.font.primary.weight.superBold};

  & * {
    font-weight: inherit;
  }

  &.is-warning {
    color: ${props => props.theme.palette.messaging.warning.normalForeground};
  }
`

const HeroPriceRing = styled.div`
  display: inline-block;
  padding: ${rem(4)} ${rem(12)};
  border: 2px solid ${props => props.theme.palette.highlight.primary.darkBackground};
  border-radius: 999px;

  &.is-warning {
    border-color: ${props => props.theme.palette.messaging.warning.normalForeground};
  }
`

const StruckPrice = styled.span`
  text-decoration: line-through;
  color: ${props => props.theme.palette.neutral.default.three};
`

interface Props {
  cabinLabel: string
  packageDescription: string
  tier: MaldivesRewardsPayTierPricing
  memberTier: App.LoyaltyTier
  balancePP: number
  isLoggedIn: boolean
  needsEnroll?: boolean
  activateMembershipLabel?: string
  onEnrollClick?: () => void
  wrongRegion?: boolean
  loyaltyCurrencyLabel?: string
  switchRegionPath?: string
  onSwitchRegion?: () => void
  offers: Array<App.LoyaltyPurchasePointsOffer>
  applyBalance: boolean
  offersFetching?: boolean
}

function LoyaltyRewardsPayCalculatorCard(props: Props) {
  const {
    cabinLabel,
    packageDescription,
    tier,
    memberTier,
    balancePP,
    isLoggedIn,
    needsEnroll = false,
    activateMembershipLabel = '',
    onEnrollClick,
    wrongRegion = false,
    loyaltyCurrencyLabel,
    switchRegionPath,
    onSwitchRegion,
    offers,
    applyBalance,
    offersFetching = false,
  } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const formatCurrency = useCurrencyFormatter({ currency: 'AUD' })
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const handleLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const calculation = useMemo<MaldivesRewardsPayCalculation | undefined>(() => {
    if (!applyBalance) {
      return undefined
    }
    return computeMaldivesRewardsPayCalculation({ tier, balancePP, offers })
  }, [applyBalance, tier, balancePP, offers])

  const shortfallPP = calculation?.shortfallPP ?? 0
  const { offer: bestPurchaseOffer } = useLoyaltyPointsPurchaseBestOffer(shortfallPP)
  const buyPointsHref = bestPurchaseOffer
    ? `/societe/buy-points?offerId=${encodeURIComponent(bestPurchaseOffer.id)}`
    : '/societe/buy-points'

  const effectivePP = wrongRegion
    ? tier.cashPP
    : (applyBalance && calculation ? calculation.effectivePP : tier.cashPP)
  const showTopUpPrompt = applyBalance && calculation && !calculation.hasEnough && !calculation.isPayInFullCheaper && !wrongRegion
  const showSavings = applyBalance && !wrongRegion && calculation && !calculation.isPayInFullCheaper && calculation.savings > 0
  const showWarning = applyBalance && !wrongRegion && calculation?.isPayInFullCheaper
  const savingsAmount = calculation ? Math.max(0, calculation.savings) : 0
  const savingsPercent = calculation?.savingsPct ?? 0

  const hotelPackageLabel = intl.formatMessage({
    id: 'dCGpO2NDjdUPG1uJ2Xf0',
    defaultMessage: 'Hotel Package + Co-Payment',
    description: 'Section label for hotel package and flight co-payment cash pricing in the Société Rewards pay calculator card',
  })
  const flightsLabel = intl.formatMessage({
    id: '3Je1x-d9uoVyKMP_1fBn',
    defaultMessage: 'Flights',
    description: 'Section label for flight points pricing in the Société Rewards pay calculator card',
  })
  const packagePriceLabel = intl.formatMessage({
    id: 'ONBlKkOpHcVdZePgJ00w',
    defaultMessage: 'Package price',
    description: 'Row label for hotel package price in the Société Rewards pay calculator card',
  })
  const coPaymentLabel = intl.formatMessage({
    id: '1KVyWrbEVfbxoQaM9IXp',
    defaultMessage: 'Co-payment',
    description: 'Row label for reward flight cash co-payment in the Société Rewards pay calculator card',
  })
  const pointsRequiredLabel = intl.formatMessage({
    id: 'UkV7CIgqthkahO-WMCdV',
    defaultMessage: 'Points required',
    description: 'Row label for reward flight points required in the Société Rewards pay calculator card',
  })
  const yourBalanceLabel = intl.formatMessage({
    id: 'wh686LdeHlyBijygjBPi',
    defaultMessage: 'Your balance',
    description: 'Row label for points applied from member balance in the Société Rewards pay calculator card',
  })
  const loginToViewLabel = intl.formatMessage({
    id: 'zp0O9z2ci6Dk9LPEDw0u',
    defaultMessage: 'Log in to view',
    description: 'Message shown on reward fare cards when the user is not logged in',
  })
  const stillToPayLabel = intl.formatMessage({
    id: 'YHQROxII5PN-yVBmoQI9',
    defaultMessage: 'Still to pay',
    description: 'Row label for remaining points shortfall in the Société Rewards pay calculator card',
  })
  const payInFullLabel = intl.formatMessage({
    id: 'BXNvILifWPyWBr2CpQHy',
    defaultMessage: 'Pay in full',
    description: 'Section heading for pay-in-full pricing in the Société Rewards pay calculator outcome column',
  })
  const societeRewardsLabel = intl.formatMessage(
    {
      id: 'zZRyin111_92YMoxOuZd',
      defaultMessage: '{loyaltyProgramName} Rewards',
      description: 'Section heading for Société Rewards pricing in the pay calculator outcome column',
    },
    { loyaltyProgramName },
  )
  const youPayLabel = intl.formatMessage({
    id: 'HptgR4uD_7LR8MeEqM0L',
    defaultMessage: 'You pay',
    description: 'Final total row label in the Société Rewards pay calculator outcome column',
  })
  const pointsTopUpLabel = intl.formatMessage({
    id: 'Cv3KAAn1NzIGI4jsOa5p',
    defaultMessage: 'Points top-up',
    description: 'Cost breakdown row label for purchased points in the Société Rewards pay calculator',
  })
  const topUpCtaLabel = intl.formatMessage(
    {
      id: '87a9q3FLljsEErQFggqK',
      defaultMessage: 'Top-up {loyaltyProgramName} Points',
      description: 'Primary CTA on the insufficient-points card on the Flash hotel offer page; navigates to the loyalty buy-points page.',
    },
    { loyaltyProgramName },
  )
  const buyPointsRowLabel = calculation?.topUp ? intl.formatMessage(
    {
      id: 'Dgx0xO6l6uoYFKlN2e9m',
      defaultMessage: 'Buy {points} pts',
      description: 'Top-up box row label showing base points pack size in the Société Rewards pay calculator',
    },
    {
      points: <FormatDecimal value={calculation.topUp.packPts} />,
    },
  ) : null
  const topUpPackCostValue = calculation?.topUp
    ? <FormatCurrency value={calculation.topUp.packCost} format="dynamic" />
    : null
  const bonusCreditedMessage = calculation?.topUp && calculation.topUp.bonus > 0 ? intl.formatMessage(
    {
      id: 'zkG2DjWdfTST0bSt9yMW',
      defaultMessage: '+ {bonus} bonus pts = {credited} pts credited',
      description: 'Explains bonus points credited after purchase in the Société Rewards pay calculator top-up box',
    },
    {
      bonus: <FormatDecimal value={calculation.topUp.bonus} />,
      credited: <FormatDecimal value={calculation.topUp.credited} />,
    },
  ) : null
  const packagePriceValue = intl.formatMessage(
    {
      id: 'UjkQDX3JLy8hKOQTBY3i',
      defaultMessage: 'From {amount}',
      description: 'Hotel package from-price in the Société Rewards pay calculator',
    },
    {
      amount: <FormatCurrency value={tier.hotelPP} format="roundedDollar" />,
    },
  )
  const coPaymentValue = <FormatCurrency value={tier.coPaymentPP} format="dynamic" />
  const pointsTopUpBreakdownLabel = calculation?.topUp
    ? (calculation.topUp.bonus > 0
        ? intl.formatMessage(
            {
              id: 'jW2aLr6Tjt7soHLGQW9F',
              defaultMessage: 'Points top-up ({base} + {bonus} bonus pts)',
              description: 'Cost breakdown row label for purchased points showing base and bonus in the Société Rewards pay calculator',
            },
            {
              base: <FormatDecimal value={calculation.topUp.packPts} />,
              bonus: <FormatDecimal value={calculation.topUp.bonus} />,
            },
          )
        : intl.formatMessage(
            {
              id: 'OaRWLfiPWge1ZYT8KWVf',
              defaultMessage: 'Points top-up ({base} pts)',
              description: 'Cost breakdown row label for purchased points without bonus in the Société Rewards pay calculator',
            },
            {
              base: <FormatDecimal value={calculation.topUp.packPts} />,
            },
          ))
    : pointsTopUpLabel
  const youPayHeroValue = intl.formatMessage(
    {
      id: 'jQ40eG1tHo4j-oqNDrhJ',
      defaultMessage: '{amount} /person',
      description: 'Circled you-pay total with per-person suffix in the Société Rewards pay calculator outcome column',
    },
    {
      amount: <FormatCurrency value={effectivePP} format="dynamic" />,
    },
  )
  const payInFullValue = intl.formatMessage(
    {
      id: 'ABp-QH42FavWx0xiwzLY',
      defaultMessage: '{amount} /person',
      description: 'Struck-through pay-in-full price with per-person suffix in the Société Rewards pay calculator',
    },
    {
      amount: <FormatCurrency value={tier.fullPP} format="roundedDollar" />,
    },
  )
  const topUpBreakdownCostValue = <FormatCurrency
    value={calculation?.topUp?.packCost ?? 0}
    format={calculation?.topUp?.packCost ? 'dynamic' : 'roundedDollar'}
  />
  const coveredByBalanceLabel = intl.formatMessage({
    id: 'vfX1qL-GoeFcjk_gtal8',
    defaultMessage: 'Covered by your balance!',
    description: 'Success pill when member balance fully covers reward flight points in the pay calculator',
  })
  const savingsAmountLabel = savingsAmount > 0 ? intl.formatMessage(
    {
      id: 'zOa80hflm8rqPqLnCz4K',
      defaultMessage: 'Save {amount} /person',
      description: 'Dollar savings pill in the Société Rewards pay calculator outcome column',
    },
    {
      amount: formatCurrency(savingsAmount, 'roundedDollar'),
    },
  ) : null
  const savingsPercentLabel = savingsPercent > 0 ? intl.formatMessage(
    {
      id: '2maV5eacUcywzM3kbyq0',
      defaultMessage: 'Save {percent}% /person',
      description: 'Percentage savings pill in the Société Rewards pay calculator outcome column',
    },
    {
      percent: savingsPercent,
    },
  ) : null
  const payInFullWarningMessage = intl.formatMessage(
    {
      id: 'K05hiZ_2hmET-slo1hm2',
      defaultMessage: 'Topping up costs {amount} more than cash here — pay in full instead.',
      description: 'Warning when buying points costs more than paying full cash on the Société Rewards pay calculator',
    },
    {
      amount: calculation
        ? formatCurrency(Math.max(0, calculation.effectivePP - tier.fullPP), 'dynamic')
        : '',
    },
  )
  const catalogPriceMessage = intl.formatMessage(
    {
      id: 'fx60vNuHSttPNIaXFJCl',
      defaultMessage: 'From {cashAmount} + {points} pts /person',
      description: 'Fallback catalog pricing when apply-balance mode is disabled on the Société Rewards pay calculator',
    },
    {
      cashAmount: formatCurrency(tier.cashPP, 'roundedDollar'),
      points: <FormatDecimal value={tier.ptsPP} />,
    },
  )

  let balanceValue: ReactNode
  if (!isLoggedIn) {
    balanceValue = (
      <TextLink size="medium" weight="bold" onClick={handleLoginClick}>
        {loginToViewLabel}
      </TextLink>
    )
  } else if (needsEnroll) {
    balanceValue = (
      <TextLink size="medium" weight="bold" onClick={onEnrollClick}>
        {activateMembershipLabel}
      </TextLink>
    )
  } else if (wrongRegion) {
    balanceValue = (
      <LoyaltySwitchRegionPrompt
        variant="inline"
        loyaltyCurrencyLabel={loyaltyCurrencyLabel}
        switchRegionPath={switchRegionPath}
        onSwitchRegion={onSwitchRegion}
      />
    )
  } else {
    balanceValue = (
      <BodyText variant="medium" weight="bold">
        <FormatLoyaltyPoints points={balancePP} abbreviated />
      </BodyText>
    )
  }

  return (
    <CalculatorCard borderRadius="L" background="normal-background">
      <CardHeader>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
          <BodyText variant="medium" weight="bold">{cabinLabel}</BodyText>
          <Caption variant="medium" colour="neutral-three">{packageDescription}</Caption>
        </Group>
      </CardHeader>
      <CardBody>
        <WorkingColumn>
          {!applyBalance && (
            <BodyText variant="medium">{catalogPriceMessage}</BodyText>
          )}
          {applyBalance && <>
            <SectionLabel variant="medium" weight="bold" colour="highlight-primary-dark">
              {hotelPackageLabel}
            </SectionLabel>
            <Row>
              <BodyText variant="medium">{packagePriceLabel}</BodyText>
              <BodyText variant="medium" weight="bold">{packagePriceValue}</BodyText>
            </Row>
            <Row>
              <BodyText variant="medium">{coPaymentLabel}</BodyText>
              <BodyText variant="medium" weight="bold">{coPaymentValue}</BodyText>
            </Row>
            <Divider />
            <SectionLabel variant="medium" weight="bold" colour="highlight-primary-dark">
              {flightsLabel}
            </SectionLabel>
            <Row>
              <BodyText variant="medium">{pointsRequiredLabel}</BodyText>
              <BodyText variant="medium" weight="bold">
                <FormatLoyaltyPoints points={tier.ptsPP} abbreviated />
              </BodyText>
            </Row>
            <Row>
              <BodyText variant="medium">{yourBalanceLabel}</BodyText>
              {balanceValue}
            </Row>
            <Divider />
            <Row>
              <BodyText variant="medium" weight="bold">{stillToPayLabel}</BodyText>
              <BodyText variant="medium" weight="bold">
                <FormatLoyaltyPoints points={calculation?.shortfallPP ?? 0} abbreviated />
              </BodyText>
            </Row>
            {calculation?.hasEnough && (
              <CoveredByBalancePill>
                <LoyaltyTierPill kind="primary" tier={memberTier}>
                  {coveredByBalanceLabel}
                </LoyaltyTierPill>
              </CoveredByBalancePill>
            )}
            {showTopUpPrompt && calculation && (
              <TopUpPrompt>
                <Group direction="vertical" gap={8}>
                  {offersFetching && (
                    <LoadingBox height={56} borderRadius="S" />
                  )}
                  {!offersFetching && calculation.topUp && (
                    <>
                      <Row>
                        <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
                          {buyPointsRowLabel}
                        </BodyText>
                        <BodyText variant="medium" weight="bold">{topUpPackCostValue}</BodyText>
                      </Row>
                      {bonusCreditedMessage && (
                        <Caption variant="medium">{bonusCreditedMessage}</Caption>
                      )}
                    </>
                  )}
                  <TextButton kind="primary" fit="full-width" to={buyPointsHref} size="small" target="_blank">
                    {topUpCtaLabel}
                  </TextButton>
                </Group>
              </TopUpPrompt>
            )}
          </>}
        </WorkingColumn>
        <OutcomeColumn>
          {applyBalance && <>
            <OutcomeSection>
              <SectionLabel variant="medium" weight="bold" colour="highlight-primary-dark">
                {payInFullLabel}
              </SectionLabel>
              <BodyText variant="medium" weight="bold">
                <StruckPrice>{payInFullValue}</StruckPrice>
              </BodyText>
            </OutcomeSection>
            <Divider />
            <OutcomeSection>
              <RewardsSectionHeader>
                <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
                <RewardsSectionLabel variant="medium" weight="bold" colour="highlight-primary-dark">
                  {societeRewardsLabel}
                </RewardsSectionLabel>
              </RewardsSectionHeader>
              <OutcomeRow>
                <OutcomeRowLabel>
                  <BodyText variant="medium" wrap="pretty">{packagePriceLabel}</BodyText>
                </OutcomeRowLabel>
                <OutcomeRowValue>
                  <BodyText variant="medium" weight="bold">{packagePriceValue}</BodyText>
                </OutcomeRowValue>
              </OutcomeRow>
              <OutcomeRow>
                <OutcomeRowLabel>
                  <BodyText variant="medium" wrap="pretty">{coPaymentLabel}</BodyText>
                </OutcomeRowLabel>
                <OutcomeRowValue>
                  <BodyText variant="medium" weight="bold">{coPaymentValue}</BodyText>
                </OutcomeRowValue>
              </OutcomeRow>
              {calculation?.topUp && !wrongRegion && (
                <OutcomeRow>
                  <OutcomeRowLabel>
                    <BodyText variant="medium" wrap="pretty">
                      {pointsTopUpBreakdownLabel}
                    </BodyText>
                  </OutcomeRowLabel>
                  <OutcomeRowValue>
                    <BodyText variant="medium" weight="bold">{topUpBreakdownCostValue}</BodyText>
                  </OutcomeRowValue>
                </OutcomeRow>
              )}
              <Divider />
              <OutcomeRow>
                <OutcomeRowLabel>
                  <BodyText variant="medium" weight="bold" wrap="pretty">{youPayLabel}</BodyText>
                </OutcomeRowLabel>
                <OutcomeRowValue>
                  <HeroPriceRing className={showWarning ? 'is-warning' : undefined}>
                    <HeroPrice
                      variant="medium"
                      weight="bold"
                      as="span"
                      className={showWarning ? 'is-warning' : undefined}
                    >
                      {youPayHeroValue}
                    </HeroPrice>
                  </HeroPriceRing>
                </OutcomeRowValue>
              </OutcomeRow>
              {showSavings && (
                <SavingsPills>
                  {savingsAmountLabel && (
                    <Pill palette="success">{savingsAmountLabel}</Pill>
                  )}
                  {savingsPercentLabel && (
                    <Pill palette="success">{savingsPercentLabel}</Pill>
                  )}
                </SavingsPills>
              )}
              {showWarning && (
                <BodyText variant="medium" colour="critical">{payInFullWarningMessage}</BodyText>
              )}
            </OutcomeSection>
          </>}
        </OutcomeColumn>
      </CardBody>
    </CalculatorCard>
  )
}

export default LoyaltyRewardsPayCalculatorCard
